'use client'

import { memo, type ReactNode } from 'react'
import {
  Crosshair,
  Globe2,
  Maximize2,
  Minimize2,
  Share2,
  Sparkles,
  Volume2,
  VolumeX,
  Wind,
} from 'lucide-react'
import type { TrackerCopy } from '@/lib/santa-tracker'

/**
 * Barra de controles del mapa: seguir al trineo, sonido, pantalla completa,
 * compartir y reducir movimiento.
 *
 * Los cinco son el mismo botón con distinto contenido, así que se describen
 * como **datos** y se pintan en un bucle: añadir un control es una entrada en
 * la lista, no otra rama de JSX.
 *
 * Botones de 44 px de lado con etiqueta visible desde `sm`; en móvil solo el
 * icono, pero siempre con `aria-label` y `title`. El sonido arranca **apagado**
 * y nunca se enciende solo.
 */

const BUTTON =
  'inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/20 sm:px-4'

const BUTTON_ACTIVE = 'border-amber-300/60 bg-amber-400/20 text-amber-50'

const ICON = 'h-4 w-4'

interface Control {
  key: string
  label: string
  icon: ReactNode
  active: boolean
  onClick: () => void
}

/** Control de dos estados: la etiqueta y el icono dependen de si está activo. */
function toggleControl(spec: {
  key: string
  on: boolean
  labelOn: string
  labelOff: string
  iconOn: ReactNode
  iconOff: ReactNode
  onClick: () => void
}): Control {
  return {
    key: spec.key,
    label: spec.on ? spec.labelOn : spec.labelOff,
    icon: spec.on ? spec.iconOn : spec.iconOff,
    active: spec.on,
    onClick: spec.onClick,
  }
}

export interface TrackerControlsProps {
  copy: TrackerCopy
  soundOn: boolean
  onToggleSound: () => void
  fullscreenSupported: boolean
  isFullscreen: boolean
  onToggleFullscreen: () => void
  onShare: () => void
  shareFeedback: boolean
  follow: boolean
  onToggleFollow: () => void
  motionOn: boolean
  onToggleMotion: () => void
}

function buildControls(props: TrackerControlsProps): Control[] {
  const { copy } = props

  const follow = toggleControl({
    key: 'follow',
    on: props.follow,
    labelOn: copy.map.wholeWorld,
    labelOff: copy.map.followSleigh,
    iconOn: <Globe2 className={ICON} aria-hidden />,
    iconOff: <Crosshair className={ICON} aria-hidden />,
    onClick: props.onToggleFollow,
  })

  const sound = toggleControl({
    key: 'sound',
    on: props.soundOn,
    labelOn: copy.controls.soundOff,
    labelOff: copy.controls.soundOn,
    iconOn: <Volume2 className={ICON} aria-hidden />,
    iconOff: <VolumeX className={ICON} aria-hidden />,
    onClick: props.onToggleSound,
  })

  const fullscreen = toggleControl({
    key: 'fullscreen',
    on: props.isFullscreen,
    labelOn: copy.controls.fullscreenExit,
    labelOff: copy.controls.fullscreenEnter,
    iconOn: <Minimize2 className={ICON} aria-hidden />,
    iconOff: <Maximize2 className={ICON} aria-hidden />,
    onClick: props.onToggleFullscreen,
  })

  const share = toggleControl({
    key: 'share',
    // `shareFeedback` solo cambia la etiqueta a «enlace copiado»; el botón no
    // se queda pulsado, así que no se marca como activo.
    on: false,
    labelOn: copy.controls.share,
    labelOff: props.shareFeedback ? copy.controls.shareCopied : copy.controls.share,
    iconOn: <Share2 className={ICON} aria-hidden />,
    iconOff: <Share2 className={ICON} aria-hidden />,
    onClick: props.onShare,
  })

  const motion = toggleControl({
    key: 'motion',
    on: props.motionOn,
    labelOn: copy.controls.motionOff,
    labelOff: copy.controls.motionOn,
    iconOn: <Wind className={ICON} aria-hidden />,
    iconOff: <Sparkles className={ICON} aria-hidden />,
    onClick: props.onToggleMotion,
  })

  // Sin soporte (Safari en iPhone, por ejemplo) el botón de pantalla completa
  // no se muestra deshabilitado: se retira. Un botón que solo puede fallar no
  // es una opción.
  const optionalFullscreen = props.fullscreenSupported ? [fullscreen] : []

  // El de movimiento se marca activo cuando está REDUCIDO (que es el estado
  // "el usuario ha tocado esto"), al revés que el resto.
  return [follow, sound, ...optionalFullscreen, share, { ...motion, active: !props.motionOn }]
}

export const TrackerControls = memo(function TrackerControls(props: TrackerControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {buildControls(props).map((control) => (
        <button
          key={control.key}
          type="button"
          onClick={control.onClick}
          aria-label={control.label}
          title={control.label}
          aria-pressed={control.active}
          className={`${BUTTON} ${control.active ? BUTTON_ACTIVE : ''}`}
        >
          {control.icon}
          <span className="hidden sm:inline">{control.label}</span>
        </button>
      ))}
    </div>
  )
})
